import axios from 'axios';
import {setErrorOverlay, setIsAuthenticated, setIsSessionTimeout} from '../store/app/actions';
import reduxStore from '../store';

import { ADMIN_API_ROOT } from '../config';

export const menuService = axios.create({
  baseURL: ADMIN_API_ROOT
});

// Response interceptor
menuService.interceptors.response.use(
  (response) => {
    if (response && response.data) {
      // Axios 1.x automatically parses JSON responses
      const data = response.data;
      if (data && data['x-auth-token']) {
        const { dispatch } = reduxStore;
        setAuthHeader(data['x-auth-token']);
        dispatch(setIsSessionTimeout(false));
        dispatch(setIsAuthenticated(true));
        if (window.sessionTimeoutTimer) {
          window.clearTimeout(window.sessionTimeoutTimer);
        }
        window.sessionTimeoutTimer = window.setTimeout(() => {
          const { dispatch } = reduxStore;
          dispatch(setIsSessionTimeout(true));
        }, 3600000); // 30 min timeout
      }
    }
    return response;
  },
  (error) => {
    const ignoreRoutes = [
      "/retailer/login","/retailer/register","/retailer/auth", "/retailer/update-password", "/retailer/reset-password", "/image"
    ]
    
    // Check if this is an ignored route
    if (error.request && error.request.responseURL) {
      for (let i = 0; i < ignoreRoutes.length; i++) {
        if (error.request.responseURL.includes(ignoreRoutes[i])) {
          return Promise.reject(error);
        }
      }
    }
    
    const { dispatch } = reduxStore;
    if (error.response && error.response.status === 403) {
      dispatch(setIsSessionTimeout(true));
    } else {
      let messages = ["An unexpected error has occurred, please try again."]
      let refID = "";
      console.log(error.response?.data);
      if (error.response && error.response.data) {
        // Axios 1.x automatically parses JSON responses
        const responseData = error.response.data;
        if (responseData) {
          if (responseData.errors) {
            messages = responseData.errors
          } else if (responseData.error) {
            messages = [responseData.error]
          }
          if (responseData.request_id) {
            refID = responseData.request_id
          }
        }
      }
      dispatch(setErrorOverlay({messages, refID}))
    }
    return Promise.reject(error);
  }
);

export const setAuthHeader = (AUTH_TOKEN, doNotSave) => {
  menuService.defaults.headers.common['Authorization'] = AUTH_TOKEN;
  if(!doNotSave) localStorage.setItem('auth_token', AUTH_TOKEN);
};

export const setStoreIdHeader = (id) => {
  menuService.defaults.headers.common['x-store-id'] = id;
  localStorage.setItem('x-store-id', id);
};

export const getStores = () => {
  return menuService.get('/retailer/locations');
};
export const updateStoreHours = (store_id, payload) => {
  return menuService.patch(`/retailer/${store_id}/hours`, payload);
}
export const getVendorProduct = (sku_id) => {
  return menuService.get(`/stockroom/product/${sku_id}`);
};

export const updateVendorProduct = (sku_id) => (payload) => {
  return menuService.post(`/retailer/product/${sku_id}`, payload);
};
export const reportProblem = (sku_id, problem, details) => {
  return menuService.post(`/retailer/report-product-issue`, {sku_id, problem, details});
}
export const getGlobalProduct = (sku_id) => {
  return menuService.get(`/stockroom/global-product/${sku_id}`)
}
export const updateRecommendationAttributes = (sku_id, recommendationPayload) => {
  return menuService.patch(`/stockroom/global-product/${sku_id}/recommendation-override`, recommendationPayload);
} 
export const updateTopPickOverride = (sku_id, top_pick_override) => {
  return menuService.patch(`/stockroom/global-product/${sku_id}/top-pick-override`, {top_pick_override})
}

export const addVendorProduct = (storeId) => (payload) => {
  return menuService.post(`/retailer/store/${storeId}/product`, payload);
};

export const patchUser = (user, payload) => {
  return menuService.patch(`/retailer/users/${user}`, payload);
}
export const getUsers = () => {
  return menuService.get('/retailer/users');
}
export const addUserPermission = (user, store,permission) => {
  return menuService.patch(`/retailer/users/${user}/permission`, {op:"add", store, permission});
}
export const removeUserPermission = (user, store,permission) => {
  return menuService.patch(`/retailer/users/${user}/permission`, {op:"remove", store, permission});
}

export const addUser = (payload) => {
  return menuService.post('/retailer/users', payload)
}
export const getSellersForConfig = (enabled, archived, feed, api) => {
  return menuService.get(`/retailer/config/list?enabled=${enabled}&archived=${archived}&feed=${feed}&api_credentials=${api}`);
}
export const getInventoryOutputFolders = () => {
  return menuService.get(`/retailer/config/output-locations`);
}
export const updateRetailerFTPConfig = (storeId, payload) => {
  return menuService.patch(`/retailer/${storeId}/ftp-config`, payload);
}
export const getPipelineEvents = (storeId) => {
  let uri = `retailer/pipeline/events`;
  if(storeId){
    uri += `?store_id=${storeId}`
  }
  return menuService.get(uri);
}
export const createRetailerFTPCredentials = (storeId) => {
  return menuService.post(`/retailer/${storeId}/ftp-config`);
}
export const previewRecommendations = (recommendationType, userId, storeId, previewPayload) => {
  switch(recommendationType){
    
  }
}
export const uploadProductImage = (sku_id, attribute, data, fileName, dataType) => {
  return menuService.post(`/admin/product/${sku_id}/image`, {attribute, data, dataType, fileName});
}
export const uploadStockCSV = (data, fileName) => {
  return menuService.post(`/retailer/stock-level/upload`, {data, fileName});
}
export const algoliaIndex = (updated, skus) => {
  return menuService.post('/stockroom/admin/index', {updated, skus});
}
export const getSupportedAPIs = () => {
  return menuService.get('/admin/credentials/types');
}
export const createRetailerAPICredentials = (storeId, payload) => {
  return menuService.post(`/admin/credentials/add/${storeId}`, payload);
}
export const updateRetailerAPICredentials = (storeId, payload) => {
  return menuService.patch(`/admin/credentials/update/${storeId}`, payload);
}
export const deleteRetailerAPICredentials = (storeId, payload) => {
  return menuService.delete(`/admin/credentials/remove/${storeId}`, payload);
}
export const testRetailerAPICredentials = (storeId, payload) => {
  return menuService.post(`/admin/credentials/test/${storeId}`, payload);
}
export const getLightspeedRetailMasterTokens = () => {
  return menuService.get('/admin/credentials/lightspeed-retail/master-tokens');
}
export const getS3Browser = () => {
  return menuService.get('/admin/s3-browser/locations');
}
export const getS3BrowserCredentials = () => {
  return menuService.get('/admin/s3-browser/credentials');
}

// Category Management API functions
export const getCategories = () => {
  return menuService.get('/admin/category');
}

export const getCategory = (categoryId) => {
  return menuService.get(`/admin/category/${categoryId}`);
}

export const updateCategory = (categoryId, payload) => {
  return menuService.patch(`/admin/category/${categoryId}`, payload);
}

export const createCategory = (payload) => {
  return menuService.post('/admin/category', payload);
}

export const archiveCategory = (categoryId, enabled) => {
  return menuService.patch(`/admin/category/${categoryId}`, { enabled });
}

export const getRestrictedCategories = (storeId) => {
  let uri = `/admin/restricted/${storeId}/categories`;
  return menuService.get(uri);
}
export const getTaxCodes = (isAlcohol) => {
  let uri = '/admin/taxcodes';
  if(isAlcohol) {
    uri += '?alcohol=true';
  }
  return menuService.get(uri);
}
export const uploadCategoryImage = (categoryId, payload) => {
  const { attribute, data, name, dataType } = payload;
  return menuService.post(`/admin/category/${categoryId}/image`, { attribute, data, dataType, name });
}
export const getLightspeedShops = (storeId, credentials) => {
  return menuService.get(`/admin/credentials/lightspeed-retail/shops/${storeId}`, {params: credentials});
}
